/* ============================================================
   AuthContext.jsx — Shared Authentication & Wishlist State
   Provides user session, bookings database, and live customer wishlist.
   ============================================================ */
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  customerLoginApi,
  customerRegisterApi,
  getCustomerProfileApi,
  updateCustomerProfileApi,
  getCustomerBookingsApi,
  cancelBookingApi,
  logoutApi,
  toggleWishlistApi,
  getCustomerWishlistApi,
  getUserWishlistHotelIdsApi,
} from "../../services/apiService";

export function formatImageUrl(path) {
  if (!path) return "";
  if (typeof path !== "string") return "";
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
    return path;
  }
  const base = (process.env.REACT_APP_API_URL || "http://20.20.20.6:4435").replace(/\/+$/, "");
  const cleanPath = path.replace(/^\/+/, "");
  return `${base}/${cleanPath}`;
}

const AuthContext = createContext();

const FALLBACK_BOOKING_IMAGE =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=500&q=80";

function normalizeBooking(b) {
  const checkIn = b.CheckInDate ? new Date(b.CheckInDate) : null;
  const checkOut = b.CheckOutDate ? new Date(b.CheckOutDate) : null;
  const fmt = (d) =>
    d
      ? d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

  const nights =
    b.NoOfNights ||
    (checkIn && checkOut
      ? Math.max(1, Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24)))
      : 1);

  const statusName = b.BookingStatusName || b.BookingStatus || "Pending";
  const statusLower = statusName.toLowerCase();
  let statusCategory = "upcoming";
  if (statusLower.includes("cancel")) {
    statusCategory = "cancelled";
  } else if (
    statusLower.includes("complete") ||
    (checkOut && checkOut < new Date())
  ) {
    statusCategory = "completed";
  }

  const rooms = Array.isArray(b.Rooms) ? b.Rooms : [];
  const roomName =
    rooms
      .map((r) => r.RoomType || r.RoomTypeName)
      .filter(Boolean)
      .join(", ") || b.BoardingDetails || "Standard Luxury Suite";

  const totalAdults =
    b.NoOfAdults || rooms.reduce((sum, r) => sum + (r.NoOfAdults || 0), 0) || 1;
  const totalChildren =
    b.NoOfChildren ||
    rooms.reduce((sum, r) => sum + (r.NoOfChildren || 0), 0) ||
    0;
  const roomCount = b.NoOfRooms || rooms.length || 1;

  // Prioritize HotelMainImage, never use HotelLogo as main thumb
  const rawImg =
    b.HotelMainImage ||
    b.MainImage ||
    b.HotelImage ||
    b.ImageUrl ||
    b.Image ||
    b.PhotoUrl ||
    (rooms[0] && (rooms[0].RoomImage || rooms[0].Image || rooms[0].MainImage)) ||
    "";

  const image = rawImg ? formatImageUrl(rawImg) : FALLBACK_BOOKING_IMAGE;

  return {
    id: b.BookingId || b.id,
    bookingNo: b.BookingNo || b.BookingRefNo,
    bookingRefNo: b.BookingRefNo || b.BookingNo,
    hotelCode: b.HotelCode,
    hotelName: b.HotelName || "LapStay Stay",
    hotelAddress: b.HotelAddress || "",
    roomName,
    image,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    dates: checkIn && checkOut ? `${fmt(checkIn)} - ${fmt(checkOut)}` : "",
    nights,
    guests: `${totalAdults} Guest${totalAdults === 1 ? "" : "s"}${
      totalChildren
        ? `, ${totalChildren} Child${totalChildren === 1 ? "" : "ren"}`
        : ""
    }, ${roomCount} Room${roomCount === 1 ? "" : "s"}`,
    totalAdults,
    totalChildren,
    roomCount,
    price: b.CustomerPrice ?? b.PayableAmount ?? b.TotalAmount ?? 0,
    totalAmount: b.TotalAmount ?? b.PayableAmount ?? 0,
    paidAmount: b.PaidAmount ?? 0,
    balanceAmount: b.BalanceAmount ?? 0,
    paymentStatus: b.PaymentStatus || "Unpaid",
    status: statusName,
    statusCategory,
    refundAmount: null,
    raw: b,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("ls_user");
    const token = localStorage.getItem("customerToken");
    return saved && token ? JSON.parse(saved) : null;
  });

  const [wishlist, setWishlist] = useState(() => {
    const saved = localStorage.getItem("ls_wishlist");
    try {
      return saved ? JSON.parse(saved).map(Number) : [];
    } catch (e) {
      return [];
    }
  });

  const [wishlistData, setWishlistData] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState(null);

  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: "booking",
      title: "Your booking is confirmed!",
      text: "Your booking for LOFT0812 is confirmed.",
      time: "10:30 AM",
      read: false,
    },
    {
      id: 2,
      type: "alert",
      title: "Check-in reminder",
      text: "Your check-in for LOFT0812 is tomorrow.",
      time: "Yesterday",
      read: false,
    },
    {
      id: 3,
      type: "offer",
      title: "Special offer for you",
      text: "Enjoy 15% off on your next booking.",
      time: "2 Days ago",
      read: false,
    },
    {
      id: 4,
      type: "payment",
      title: "Payment Successful",
      text: "Payment of ₹11,810 for LOFT0812 was successful.",
      time: "3 Days ago",
      read: true,
    },
  ]);

  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const openLoginModal = () => window.dispatchEvent(new Event("open-login-modal"));
  const closeLoginModal = () => window.dispatchEvent(new Event("close-login-modal"));

  function showToast(msg, type = "success") {
    setToastMessage(msg);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3500);
  }

  useEffect(() => {
    if (user) {
      localStorage.setItem("ls_user", JSON.stringify(user));
    } else {
      localStorage.removeItem("ls_user");
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem("ls_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  async function fetchBookings() {
    const token = localStorage.getItem("customerToken");
    if (!token) return;

    setBookingsLoading(true);
    setBookingsError(null);
    try {
      const payload = {
        email: user?.email || user?.Email || user?.EmailId || localStorage.getItem("lapstay_last_email") || "",
        customerId: user?.id || user?.CustomerId || localStorage.getItem("customer_id")
      };
      console.log("FETCH BOOKINGS PAYLOAD ==>", payload);
      const response = await getCustomerBookingsApi(payload);
      console.log("CUSTOMER BOOKINGS RESPONSE ==>", response);

      if (
        response &&
        response.Status &&
        Array.isArray(response.ResponsePacket)
      ) {
        const mapped = response.ResponsePacket.map(normalizeBooking)
          .sort((a, b) => (b.id || 0) - (a.id || 0));
        setBookings(mapped);
      } else if (response && response.Status === false) {
        setBookings([]);
        setBookingsError(response.Message || "Could not load your bookings.");
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error("Fetch Bookings Error:", err);
      const serverMsg = err?.response?.data?.Message;
      setBookings([]);
      setBookingsError(
        serverMsg || "Could not load your bookings. Please try again."
      );
    } finally {
      setBookingsLoading(false);
    }
  }

  async function fetchWishlist() {
    const userId = Number(user?.id || user?.userId || user?.CustomerId || localStorage.getItem("customer_id") || 0);
    if (!userId) return;

    setWishlistLoading(true);
    try {
      const [idsRes, fullRes] = await Promise.allSettled([
        getUserWishlistHotelIdsApi({ UserId: userId }),
        getCustomerWishlistApi({ UserId: userId, PageNo: 1, RecordsPerPage: 50 }),
      ]);

      if (idsRes.status === "fulfilled" && idsRes.value?.ResponsePacket?.WishlistedHotelIds) {
        const ids = idsRes.value.ResponsePacket.WishlistedHotelIds.map(Number);
        setWishlist(ids);
        localStorage.setItem("ls_wishlist", JSON.stringify(ids));
      }

      if (fullRes.status === "fulfilled" && fullRes.value?.ResponsePacket?.Data) {
        const list = Array.isArray(fullRes.value.ResponsePacket.Data)
          ? fullRes.value.ResponsePacket.Data
          : [];
        setWishlistData(list);
        if (!idsRes.value?.ResponsePacket?.WishlistedHotelIds && list.length > 0) {
          const derivedIds = list.map((item) => Number(item.HotelId || item.hotelId)).filter(Boolean);
          setWishlist(derivedIds);
          localStorage.setItem("ls_wishlist", JSON.stringify(derivedIds));
        }
      }
    } catch (err) {
      console.error("Fetch Wishlist Error:", err);
    } finally {
      setWishlistLoading(false);
    }
  }

  async function toggleWishlist(hotelId) {
    const numId = Number(hotelId);
    if (!numId) return;

    const token = localStorage.getItem("customerToken") || localStorage.getItem("sessionToken");
    const userId = Number(user?.id || user?.userId || user?.CustomerId || localStorage.getItem("customer_id") || 0);

    // Authentication Guard: Unauthenticated users are not allowed to modify wishlist
    if (!user || !token || !userId) {
      showToast("Please log in to add to your wishlist.", "error");
      window.dispatchEvent(new Event("open-login-modal"));
      return;
    }

    const isAlreadySaved = wishlist.includes(numId);
    const nextWishlist = isAlreadySaved
      ? wishlist.filter((id) => id !== numId)
      : [...wishlist, numId];

    setWishlist(nextWishlist);
    localStorage.setItem("ls_wishlist", JSON.stringify(nextWishlist));

    try {
      const res = await toggleWishlistApi({ UserId: userId, HotelId: numId });
      console.log("🌟 [AuthContext] ToggleWishlist API response:", res);

      const msg = res?.Message || res?.ResponsePacket?.Message || (isAlreadySaved ? "Removed from Wishlist" : "Added to Wishlist successfully!");
      showToast(msg, "success");

      // Refresh full wishlist data in background
      fetchWishlist();
    } catch (err) {
      console.error("Toggle Wishlist Error:", err);
      showToast("Failed to update wishlist. Please try again.", "error");
      fetchWishlist();
    }
  }

  /**
   * Cancels a booking via POST /api/booking/cancel. On success, patches the
   * matching booking in local state to "Cancelled" (with refund amount)
   * instead of refetching the whole list, so the UI updates instantly.
   */
  async function cancelBooking(bookingId, reason) {
    try {
      const payload = { bookingId, reason };
      console.log("CANCEL BOOKING PAYLOAD ==>", payload);
      const response = await cancelBookingApi(payload);
      console.log("CANCEL BOOKING RESPONSE ==>", response);

      if (response && response.Status && response.ResponsePacket) {
        const packet = response.ResponsePacket;
        setBookings((prev) =>
          prev.map((b) =>
            b.id === bookingId
              ? {
                  ...b,
                  status: "Cancelled",
                  statusCategory: "cancelled",
                  refundAmount: packet.refundAmount ?? null,
                }
              : b
          )
        );
        const msg =
          packet.message ||
          `Booking cancelled successfully.${
            packet.refundAmount
              ? ` Refund of ₹${packet.refundAmount.toLocaleString(
                  "en-IN"
                )} initiated.`
              : ""
          }`;
        showToast(msg, "success");
        return {
          success: true,
          refundAmount: packet.refundAmount,
          message: msg,
        };
      } else {
        const msg =
          response?.Message ||
          "Could not cancel this booking. Please try again.";
        showToast(msg, "error");
        return { success: false, message: msg };
      }
    } catch (err) {
      console.error("Cancel Booking Error:", err);
      const serverMsg = err?.response?.data?.Message;
      const msg =
        serverMsg || "Could not cancel this booking. Please try again.";
      showToast(msg, "error");
      return { success: false, message: msg };
    }
  }

  useEffect(() => {
    if (user && localStorage.getItem("customerToken")) {
      fetchBookings();
      fetchWishlist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function login(email, password) {
    try {
      const payload = { Identifier: email, Password: password };
      if (email.includes("@")) localStorage.setItem("lapstay_last_email", email);
      console.log("LOGIN PAYLOAD ==>", payload);
      const response = await customerLoginApi(payload);
      console.log("LOGIN RESPONSE ==>", response);

      if (response && response.Status && response.ResponsePacket) {
        localStorage.setItem("customerToken", response.ResponsePacket.Token);
        localStorage.setItem("sessionToken", response.ResponsePacket.Token);
        window.dispatchEvent(new Event("auth-changed"));
        window.dispatchEvent(new Event("user-login"));
        localStorage.setItem(
          "customer_id",
          response.ResponsePacket.Customer?.CustomerId || ""
        );
        const c = response.ResponsePacket.Customer;
        if (c) {
          const initials = c.FullName
            ? c.FullName.split(" ")
                .map((w) => w[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)
            : "US";
          setUser({
            id: c.CustomerId,
            name: c.FullName,
            email: c.Email || c.EmailId || email || "",
            phone: c.ContactNo,
            photo: c.ProfilePhoto || null,
            dob: c.DOB || null,
            gender: c.Gender || null,
            initials,
            role: "Customer",
          });
          const welcomeMsg = `Welcome back, ${c.FullName}! 👋`;
          showToast(welcomeMsg, "success");
          fetchBookings();
          fetchWishlist();
          return { success: true, message: welcomeMsg };
        }
        const profileRes = await getCustomerProfileApi();
        if (profileRes?.Status && profileRes.ResponsePacket) {
          const p =
            profileRes.ResponsePacket.Customer || profileRes.ResponsePacket;
          const initials = p.FullName
            ? p.FullName.split(" ")
                .map((w) => w[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)
            : "US";
          setUser({
            id: p.CustomerId,
            name: p.FullName,
            email: p.Email || p.EmailId || "",
            phone: p.ContactNo,
            photo: p.ProfilePhoto || null,
            initials,
            role: "Customer",
          });
          const welcomeMsg = `Welcome back, ${p.FullName}! 👋`;
          showToast(welcomeMsg, "success");
          fetchBookings();
          fetchWishlist();
          return { success: true, message: welcomeMsg };
        }
        showToast("Login failed. Please try again.", "error");
        return { success: false, message: "Login failed. Please try again." };
      } else {
        const msg = response?.Message || "Invalid email/mobile number or password.";
        showToast(msg, "error");
        return { success: false, message: msg };
      }
    } catch (err) {
      console.error("Login Error:", err);
      const errData = err?.response?.data || {};
      const serverMsg = errData?.Message || (errData?.Errors && errData.Errors[0]?.msg);
      const msg =
        serverMsg ||
        (err?.response?.status === 401
          ? "Invalid email/mobile number or password."
          : "Login failed. Please check your credentials.");
      showToast(msg, "error");
      return { success: false, message: msg };
    }
  }

  async function register(name, email, phone, password, dob = "", gender = "") {
    try {
      const payload = {
        FullName: name,
        Email: email,
        ContactNo: phone,
        Password: password,
        ...(dob && { DOB: dob }),
        ...(gender && { Gender: gender }),
      };
      console.log("REGISTER PAYLOAD ==>", payload);
      const response = await customerRegisterApi(payload);
      console.log("REGISTER RESPONSE ==>", response);

      if (response && response.Status) {
        return { success: true, message: response.Message || "Registration successful! Please login to continue." };
      } else {
        const msg = response?.Message || (response?.Errors && response.Errors[0]?.msg) || "Registration failed. Please try again.";
        return { success: false, message: msg };
      }
    } catch (err) {
      console.error("Register Error:", err);
      const errData = err?.response?.data || {};
      const msg = errData?.Message || (errData?.Errors && errData.Errors[0]?.msg) || "Registration failed. Please try again.";
      return { success: false, message: msg };
    }
  }

  async function logout() {
    try {
      await logoutApi();
    } catch (e) {}
    setUser(null);
    setBookings([]);
    setBookingsError(null);
    setWishlist([]);
    setWishlistData([]);
    localStorage.removeItem("customerToken");
    localStorage.removeItem("customer_id");
    localStorage.removeItem("ls_user");
    localStorage.removeItem("authUser");
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("ls_wishlist");
    showToast("You have been logged out successfully!", "success");
    window.location.href = "/";
    return true;
  }

  async function confirmLogout() {
    return logout();
  }

  function addBooking(booking) {
    setBookings((prev) => [booking, ...prev]);
  }

  async function updateProfile(updatedFields) {
    try {
      const payload = {};
      if (updatedFields.name) payload.FullName = updatedFields.name;
      if (updatedFields.dob) payload.DOB = updatedFields.dob;
      if (updatedFields.gender) payload.Gender = updatedFields.gender;
      if (updatedFields.photo) payload.ProfilePhoto = updatedFields.photo;
      if (updatedFields.curPass)
        payload.CurrentPassword = updatedFields.curPass;
      if (updatedFields.newPass) payload.NewPassword = updatedFields.newPass;

      console.log("UPDATE PROFILE PAYLOAD ==>", payload);
      const response = await updateCustomerProfileApi(payload);
      console.log("UPDATE PROFILE RESPONSE ==>", response);

      if (response && response.Status && response.ResponsePacket) {
        const c = response.ResponsePacket.Customer || {};
        setUser((prev) => {
          if (!prev) return null;
          const name = c.FullName || prev.name;
          const initials =
            name
              .split(" ")
              .map((w) => w[0])
              .join("")
              .toUpperCase()
              .slice(0, 2) || "US";
          return {
            ...prev,
            name,
            email: c.Email || prev.email,
            phone: c.ContactNo || prev.phone,
            photo: c.ProfilePhoto || updatedFields.photo || prev.photo,
            dob: c.DOB || updatedFields.dob || prev.dob,
            gender: c.Gender || updatedFields.gender || prev.gender,
            initials,
          };
        });
        showToast(
          response.Message || "Profile Updated Successfully!",
          "success"
        );
        return true;
      } else {
        const msg = response?.Message || "Profile update failed.";
        showToast(msg, "error");
        return false;
      }
    } catch (err) {
      console.error("Update Profile Error:", err);
      const serverMsg = err?.response?.data?.Message;
      showToast(serverMsg || "Profile update failed.", "error");
      return false;
    }
  }

  function markNotificationsRead() {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        wishlist,
        wishlistData,
        wishlistLoading,
        fetchWishlist,
        bookings,
        bookingsLoading,
        bookingsError,
        fetchBookings,
        cancelBooking,
        notifications,
        login,
        register,
        logout,
        confirmLogout,
        showLogoutConfirm,
        setShowLogoutConfirm,
        toggleWishlist,
        addBooking,
        updateProfile,
        markNotificationsRead,
        showToast,
        openLoginModal,
        closeLoginModal,
      }}
    >
      {children}

      {toastMessage && (
        <div
          style={{
            position: "fixed",
            top: "24px",
            right: "24px",
            background: toastType === "error" ? "#FF4D4D" : "#FFB507",
            color: toastType === "error" ? "#fff" : "#0A0A0A",
            padding: "14px 22px",
            borderRadius: "12px",
            fontWeight: "600",
            zIndex: 1999999999,
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontFamily: "'Poppins', sans-serif",
            fontSize: "14px",
            animation: "lrm-pop 0.3s ease-out",
            maxWidth: "360px",
          }}
        >
          <span>{toastType === "error" ? "❌" : "✅"}</span>
          {toastMessage}
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
